import { isDefiniteShopRejection } from "./shopMutationOutcome";
import React, { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { ShopApiError, shopAdminApi, type ShopCatalogDeletionCheck, type ShopCatalogKind } from "./shopAdminApi";

const reasonLabels: Record<string, string> = {
  writer_migration_required: "Skyddade lager- och sortimentskrivningar måste vara införda och låsta innan servern tillåter borttagning.",
  ambiguous_id: "Flera dokument använder samma ID. Kopplingarna måste granskas.", invalid_id: "Postens ID är ogiltigt och måste granskas.",
  linked_packages: "Produkten har kopplade förpackningar.", linked_boxes: "Förpackningen har kopplade boxar.",
  linked_stock: "Posten används i Processat lager, även om tillgängligt antal är noll.",
  order_history: "Posten behövs för orderhistorik.", reservation_history: "Posten behövs för reservationshistorik.",
  attempt_history: "Posten behövs för checkout-historik.", inventory_history: "Posten behövs för lagerhistorik.",
  history_unresolved: "Äldre historik har ofullständiga kopplingar. Borttagning kräver en kontrollerad granskning.",
  audit_limit: "Historiken är för stor för denna kontroll. Behåll posten och kontakta ansvarig utvecklare.",
};

export default function WebshopCatalogDeletion({ kind, documentId, name, user, onClose, onDeleted }: {
  kind: ShopCatalogKind; documentId: string; name: string; user: User | null | undefined; onClose: () => void; onDeleted?: () => void;
}) {
  const [check, setCheck] = useState<ShopCatalogDeletionCheck | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [reload, setReload] = useState(0);
  const intent = useRef<{ uid: string; kind: ShopCatalogKind; id: string; key: string; uncertain?: boolean;
    body: Parameters<typeof shopAdminApi.deleteCatalogRecord>[3] } | null>(null);
  const inFlight = useRef(false);
  const noun = kind === "product" ? "produkt" : kind === "package" ? "förpackning" : "batch";
  useEffect(() => {
    let active = true;
    setCheck(null); setReason(""); setError(""); setPending(!!intent.current); setConflict(false);
    if (!user) { setError("Logga in för att kontrollera aktuell behörighet."); return; }
    shopAdminApi.catalogDeletionCheck(user, kind, documentId).then((result) => { if (active) setCheck(result); })
      .catch(() => { if (active) setError("Kontrollen kunde inte hämtas. Kontrollera API-anslutningen och din behörighet; ingen post har tagits bort av dialogen."); });
    return () => { active = false; };
  }, [kind, documentId, user, reload]);

  const remove = async () => {
    if (!user || conflict || inFlight.current) return;
    if (!intent.current) {
      if (!check?.canDelete || !reason.trim()) return;
      intent.current = { uid: user.uid, kind, id: documentId, key: crypto.randomUUID(),
        body: { expectedDocumentVersion: check.expectedDocumentVersion, reason: reason.trim() } };
    }
    const attempt = intent.current;
    if (attempt.uid !== user.uid || attempt.kind !== kind || attempt.id !== documentId) {
      setError("Återgå till posten och användaren som påbörjade borttagningen för att bekräfta samma åtgärd."); return;
    }
    inFlight.current = true; setBusy(true); setPending(true); setError("");
    try {
      await shopAdminApi.deleteCatalogRecord(user, attempt.kind, attempt.id, attempt.body, attempt.key);
      intent.current = null; setPending(false);
      onDeleted?.(); onClose();
    } catch (caught) {
      if (isDefiniteShopRejection(caught, attempt.uncertain)) {
        intent.current = null; setPending(false); setConflict(true);
        setError(caught instanceof ShopApiError && (caught.status === 401 || caught.status === 403) ? "Du saknar aktuell behörighet att ta bort posten." :
          "Posten eller dess kopplingar har ändrats, eller borttagningen är spärrad. Hämta aktuell kontroll innan du fortsätter.");
      } else { attempt.uncertain = true; setError("Borttagningen kunde inte bekräftas. Försök igen med samma uppgifter och nyckel; servern utför åtgärden högst en gång."); }
    } finally { inFlight.current = false; setBusy(false); }
  };
  return <Dialog open onClose={() => { if (!inFlight.current && !intent.current) onClose(); }} fullWidth maxWidth="sm"
    aria-labelledby="shop-catalog-delete-title" aria-describedby="shop-catalog-delete-help">
    <DialogTitle id="shop-catalog-delete-title">Ta bort {noun}?</DialogTitle>
    <DialogContent>
      <p><strong>{name}</strong></p>
      <p id="shop-catalog-delete-help">Servern kontrollerar kopplingar och historik igen vid borttagning. {kind === "processing-batch" ?
        "Behåll en använd batch. Skapa en ny batch för en annan råvara eller process." :
        "För en använd post: gör produkten privat eller förpackningen inaktiv i källregistret."} Lager och orderhistorik behålls.</p>
      {error && <Alert severity="error" role="alert">{error}</Alert>}
      {!check && !error && <div role="status"><CircularProgress size={24} /> Kontrollerar kopplingar…</div>}
      {check && !check.canDelete && <Alert severity="warning"><ul>{check.blockReasons.map((value) => <li key={value}>{reasonLabels[value]}</li>)}</ul></Alert>}
      {check?.canDelete && <>
        <Alert severity="warning">Posten saknar blockerande kopplingar enligt den aktuella kontrollen. Borttagning är permanent.
          {kind !== "processing-batch" && " Produktbilder behålls för separat, granskad städning."}</Alert>
        <TextField label="Orsak till borttagning" value={reason} required fullWidth multiline minRows={2}
          disabled={busy || pending || conflict} inputProps={{ maxLength: 500 }} margin="normal"
          helperText="Sparas i privat revisionshistorik. Högst 500 tecken."
          onChange={(event) => { setReason(event.target.value); }} />
      </>}
    </DialogContent>
    <DialogActions sx={{ flexWrap: "wrap" }}>
      <Button onClick={onClose} disabled={busy || pending}>Stäng</Button>
      <Button onClick={() => setReload((current) => current + 1)} disabled={busy || pending}>Hämta aktuell kontroll</Button>
      <Button color="error" variant="contained" onClick={() => void remove()}
        disabled={busy || conflict || (!pending && (!check?.canDelete || !reason.trim()))}>{busy ? "Tar bort…" : pending ? "Försök igen" : `Ta bort ${noun}`}</Button>
    </DialogActions>
  </Dialog>;
}
